import { computed, ref, toRef, unref, type MaybeRef, type Ref } from "vue";

export type FieldValidationState = "idle" | "success" | "error" | "warning";

export type WithWarning = {
  test: boolean;
  message: string;
} | null;

export interface UseFieldValidationOptions {
  value: Ref<unknown>;
  errorMessage: Ref<string | undefined | null>;
  metaTouched?: MaybeRef<boolean>;
  touched?: MaybeRef<boolean>;
  required?: MaybeRef<boolean>;
  isFocused?: MaybeRef<boolean>;
  withWarning?: MaybeRef<WithWarning | undefined>;
}

export function useFieldValidation(options: UseFieldValidationOptions) {
  const withWarningRef: any = options.withWarning ? toRef(options, "withWarning") : ref(undefined);

  const state = computed<FieldValidationState>(() => {
    const hasValue =
      options.value?.value !== null &&
      options.value?.value !== undefined &&
      options.value?.value !== "" &&
      !(Array.isArray(options.value?.value) && options.value?.value.length === 0);
    const touchedOpt = options.touched;
    const metaTouchedOpt = options.metaTouched;
    const isFocused = unref(options.isFocused);
    const isTouched = Boolean(unref(touchedOpt)) || Boolean(unref(metaTouchedOpt));
    const isRequired = Boolean(unref(options.required));

    if (!hasValue && isTouched && isRequired) {
      return "error";
    }
    if (!hasValue && !isTouched) {
      return "idle";
    }
    if (withWarningRef.value?.test) {
      return "success";
    }
    if (options.errorMessage?.value) {
      if (isFocused) {
        return "warning";
      }
      return "error";
    }
    return "success";
  });

  const icon = computed<string>(() => {
    switch (state.value) {
      case "success":
        return "mdi-check-circle";
      case "error":
        return "mdi-close-circle";
      case "warning":
        return "mdi-progress-alert";
      default:
        return "mdi-progress-helper";
    }
  });

  const color = computed<string>(() => {
    switch (state.value) {
      case "success":
        return "success";
      case "error":
        return "error";
      case "warning":
        return "warning";
      default:
        return "grey";
    }
  });

  return { state, icon, color } as const;
}

export interface UseFieldStyleOptions {
  transparent?: boolean;
  disabled?: boolean;
  required?: boolean;
  waitingForBackend?: boolean;
  state: Ref<FieldValidationState>;
  baseClass?: string | string[];
  disabledBg?: string;
  defaultBg?: string;
}

export function useFieldStyles(options: UseFieldStyleOptions) {
  const bgColor = computed<string>(() => {
    if (options.transparent) return "transparent";
    if (options.disabled) return options.disabledBg ?? "surface/25";
    return options.defaultBg ?? "surface";
  });

  const classList = computed(() => {
    let base: string[] = [];
    if (Array.isArray(options.baseClass)) {
      base = options.baseClass;
    } else if (options.baseClass) {
      base = [options.baseClass];
    }

    return [
      "tw:transition-all tw:duration-300 tw:ease-out",
      {
        "v-input--transparent": options.transparent,
        "v-input--required": options.required,
        "v-input--waiting-for-backend": Boolean(options.waitingForBackend),
        "v-input--warning": options.state.value === "warning",
        "v-field--error": options.state.value === "error",
        "v-field--success": options.state.value === "success",
      },
      ...base,
    ];
  });

  return { bgColor, classList } as const;
}
